import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import {
  ICategorySubCategoryMappingOnboardPostBody,
  ISubCategoryListResponseBody,
} from "@/api_framework/api_modals/Inventory";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import PaginationComponent from "@/app/components/ui/Pagination";
import { useCategoryListingAPI } from "@/app/hooks/api_hooks/Inventory/Category/useCategoryListingAPI";
import { useCategoryMappingDetailAPI } from "@/app/hooks/api_hooks/Inventory/CategorySubCategoryMapping/useCategoryMappingDetailAPI";
import { useCategoryMappingOnboardAPI } from "@/app/hooks/api_hooks/Inventory/CategorySubCategoryMapping/useCategoryMappingOnboardAPI";
import { useCategoryMappingUpdateAPI } from "@/app/hooks/api_hooks/Inventory/CategorySubCategoryMapping/useCategoryMappingUpdateAPI";
import { useSubCategoryListAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryListAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { TbCircleLetterC } from "react-icons/tb";
import { useNavigate, useParams } from "react-router";

const PageSize = 10;

export interface ICategorySubCategoryMappingProps {
  category: string;
  subCategory: string[];
}

const CategorySubCategoryMappingOnboard = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchSubQuery, setSearchSubQuery] = useState<string>("");
  const [currentCategoryPage, setCurrentCategoryPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<ISubCategoryListResponseBody[]>([]);
  const { execute: fetchCategory } = useCategoryListingAPI();
  const { execute: fetchSubCategory } = useSubCategoryListAPI();
  const { categoryList, subCategoryList } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const { execute: addCategoryMapping } = useCategoryMappingOnboardAPI();
  const { execute: fetchCategoryMappingDetail, categoryMappingDetail } = useCategoryMappingDetailAPI();
  const { execute: updateCategoryMapping } = useCategoryMappingUpdateAPI();

  const formHook = useForm<ICategorySubCategoryMappingProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (params["category_mapping_id"]) {
      fetchCategoryMappingDetail(params["category_mapping_id"]);
    }
  }, []);

  useEffect(() => {
    if (categoryMappingDetail) {
      formHook.setValue("category", categoryMappingDetail?.category_name);
      const subCategoryNames = categoryMappingDetail?.sub_categories.map((sub) => sub.sub_category_name) || [];
      formHook.setValue("subCategory", subCategoryNames);
      const selectedSubCategoryIds = categoryMappingDetail?.sub_categories.map((sub) => sub.sub_category_id);
      setSelectedSubCategory(() => {
        return selectedSubCategoryIds.map((id) => ({
          subcategory_id: id,
          subcategory_name:
            categoryMappingDetail?.sub_categories.find((sub) => sub.sub_category_id === id)?.sub_category_name || "",
          subcategory_tagging: "",
          emr_search: "",
        }));
      });
    }
  }, [categoryMappingDetail]);

  useEffect(() => {
    if (categoryMappingDetail && categoryMappingDetail.category_id) {
      setSelectedCategory(categoryMappingDetail.category_id);
    } else if (categoryList?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoryList.data[0].id);
    }
  }, [categoryList, categoryMappingDetail, selectedCategory]);

  useEffect(() => {
    fetchCategory(currentCategoryPage, PageSize, searchQuery);
    fetchSubCategory(undefined, undefined, searchSubQuery);
  }, [currentCategoryPage, searchQuery, searchSubQuery]);

  useEffect(() => {
    if (categoryList?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoryList.data[0].id);
    }
  }, [categoryList, selectedCategory]);

  const handleCategorySelect = (id: string) => {
    setSelectedCategory(id);
  };

  const handleSubCategorySelect = (cat: ISubCategoryListResponseBody) => {
    setSelectedSubCategory((prev) => {
      const isAlreadySelected = prev.some((c) => c.subcategory_id === cat.subcategory_id);

      if (isAlreadySelected) {
        return prev.filter((subCat) => subCat.subcategory_id !== cat.subcategory_id);
      } else {
        return [...prev, cat];
      }
    });
  };

  const onSubmit = (data: ICategorySubCategoryMappingProps) => {
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    if (selectedSubCategory.length === 0) {
      toast.error("Please select at least one subcategory");
      return;
    }
    const selectedSubCategoryNames = selectedSubCategory?.map((subCat) => subCat?.subcategory_id);
    if (data) {
      const constructedBody: ICategorySubCategoryMappingOnboardPostBody = {
        category_id: selectedCategory,
        sub_category_ids: selectedSubCategoryNames,
      };
      if (params["category_mapping_id"]) {
        updateCategoryMapping(constructedBody).then(() => {
          navigate(`/app/inventory/category-mapping`);
        });
      } else {
        addCategoryMapping(constructedBody).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/category-mapping`);
          }
        });
      }
    }
  };

  const remainingSubCategories = subCategoryList?.data?.filter(
    (subCat) => !selectedSubCategory.some((selected) => selected.subcategory_id === subCat.subcategory_id),
  );

  return (
    <>
      <div className="sm:flex sm:items-center bg-white px-4 sm:px-6 lg:px-8 py-3">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-sm text-gray-900 leading-6">VMS Category to Sub-Category Mapping</h1>
        </div>
      </div>
      <form onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 pb-24 rounded-md shadow-sm">
          <div className="flex flex-col gap-6 sm:flex-row sm:justify-between sm:gap-8">
            <div className="w-full">
              <div>
                <InputWithAddOn
                  onEnterKeyPress={(query: string) => setSearchQuery(query)}
                  disabled={formHook.watch("category")?.length > 0}
                  containerClassName="w-full"
                  addon={<TbCircleLetterC className="w-4 h-4" />}
                  placeholder="Search by Category Name"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div>
                <div className="bg-[rgb(244,248,255)] mt-5 p-3">
                  <p className="text-center font-medium text-sm">Categories</p>
                </div>
                {formHook.watch("category")?.length > 0 ? (
                  <div
                    key={categoryMappingDetail.category_id}
                    className="flex gap-10 border-b-2 pb-3 items-center pt-3 pl-2"
                  >
                    <input type="radio" checked={true} disabled={true} />
                    <span className="font-normal text-xs">{categoryMappingDetail?.category_name}</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-col mt-5">
                      {categoryList?.data?.map((cat) => {
                        return (
                          <div key={cat?.id} className="flex gap-10 border-b-2 pb-3 items-center pt-3 pl-2">
                            <input
                              type="radio"
                              checked={selectedCategory === cat?.id}
                              onChange={() => handleCategorySelect(cat?.id)}
                            />
                            <span className="font-normal text-xs">{cat?.category_name}</span>
                          </div>
                        );
                      })}
                    </div>
                    {categoryList?.item_count && searchQuery.trim() === "" && (
                      <div className="flex justify-center items-center p-4 w-full">
                        <PaginationComponent
                          className="pagination-bar"
                          currentPage={currentCategoryPage}
                          totalCount={categoryList?.item_count}
                          pageSize={PageSize}
                          onPageChange={(page) => setCurrentCategoryPage(page)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="border-gray-300 mx-4 border-l-2"></div>
            <div className="w-full">
              <div>
                <InputWithAddOn
                  onEnterKeyPress={(query: string) => setSearchSubQuery(query)}
                  containerClassName="w-full"
                  addon={<TbCircleLetterC className="w-4 h-4" />}
                  placeholder="Search by Sub Category Name"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div>
                <div className="bg-[rgb(244,248,255)] mt-5 p-3">
                  <p className="text-center font-medium text-sm">Sub Categories</p>
                </div>
                <div className="flex flex-col mt-5 max-h-[560px] overflow-y-auto">
                  {selectedSubCategory.map((sub) => (
                    <div key={sub.subcategory_id} className="flex gap-10 border-b-2 pb-3 items-center pt-3 pl-2">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => handleSubCategorySelect(sub)}
                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                      />
                      <span className="font-normal text-xs">{sub.subcategory_name}</span>
                    </div>
                  ))}
                  {remainingSubCategories?.map((sub) => (
                    <div key={sub.subcategory_id} className="flex gap-10 border-b-2 pb-3 items-center pt-3 pl-2">
                      <input
                        type="checkbox"
                        checked={selectedSubCategory.some((selected) => selected.subcategory_id === sub.subcategory_id)}
                        onChange={() => handleSubCategorySelect(sub)}
                        className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                      />
                      <span className="font-normal text-xs">{sub.subcategory_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="fixed inset-x-0 bottom-0 z-20 flex justify-end items-center gap-4 border-t-2 w-full p-4 bg-white">
              <button
                type="button"
                onClick={() => navigate(getRouteKey("CATEGORY_MAPPING", "url"))}
                className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}
              >
                Back
              </button>
              <button type="submit" className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`}>
                Save
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default CategorySubCategoryMappingOnboard;
